import apiClient from "./client";
import { appAbsoluteUrl } from "@/utils/url";

/**
 * Get current subscription for the active tenant
 * @returns {Promise<Object>} Current subscription with plan details and usage statistics
 */
export async function getCurrentSubscription() {
  const response = await apiClient.get("/subscriptions/current/?include=plan");
  const { data, included, meta } = response.data;

  // Parse included plan
  const plan = included?.find((item) => item.type === "subscription_plan");

  return {
    id: data.id,
    ...data.attributes,
    plan: plan ? { id: plan.id, ...plan.attributes } : null,
    usage: meta?.usage || null,
  };
}

/**
 * Get all available subscription plans
 * @returns {Promise<Array>} List of all active plans
 */
export async function getAllPlans() {
  const response = await apiClient.get("/subscription-plans/");
  return response.data.data.map((item) => ({
    id: item.id,
    ...item.attributes,
  }));
}

/**
 * Get the active plans as the public pricing pages see them, without a
 * session or a tenant (`GET /api/public/plans/`), each with its `trial_days`.
 * @returns {Promise<Array>} Active plans, display-safe fields only
 */
export async function getPublicPlans() {
  const response = await apiClient.get("/public/plans/");
  return response.data.plans;
}

/**
 * Assign subscription plan to a tenant (Super Admin only)
 * @param {Object} data - Assignment data
 * @param {string} data.tenant - Tenant ID
 * @param {string} data.plan - Plan ID
 * @param {string} data.billing_cycle - Billing cycle (monthly/yearly/lifetime)
 * @param {string} data.start_date - Start date
 * @param {string} [data.end_date] - End date (optional)
 * @param {boolean} [data.auto_renew] - Auto-renew flag (optional)
 * @returns {Promise<Object>} Created subscription
 */
export async function assignPlan(data) {
  const response = await apiClient.post("/subscriptions/assign/", data);
  return response.data;
}

/**
 * Create a Stripe Checkout session for subscribing to a plan
 * @param {Object} data
 * @param {string} data.plan_id - Plan UUID
 * @param {string} data.billing_cycle - 'monthly' or 'yearly'
 * @returns {Promise<{checkout_url: string}>}
 */
export async function createCheckoutSession(data) {
  const response = await apiClient.post("/subscriptions/create-checkout-session/", {
    plan_id: data.plan_id,
    billing_cycle: data.billing_cycle,
    success_url: appAbsoluteUrl("/settings/subscription?checkout=success"),
    cancel_url: appAbsoluteUrl("/settings/subscription?checkout=canceled"),
  });
  return response.data;
}

/**
 * Move an existing subscription to a different plan, without leaving the app.
 *
 * Unlike checkout, this modifies the subscription already in place - checkout
 * would start a second one and bill the customer twice (graphit-backend#248).
 * Only usable once a subscription exists; a tenant still on trial has nothing
 * to modify and has to go through checkout.
 *
 * @param {Object} data
 * @param {string} data.plan_id - Plan UUID
 * @param {string} data.billing_cycle - 'monthly' or 'yearly'
 * @returns {Promise<{direction: string, effective: string, effective_at: number|null, plan: Object, message: string}>}
 */
export async function changePlan(data) {
  const response = await apiClient.post("/subscriptions/change-plan/", {
    plan_id: data.plan_id,
    billing_cycle: data.billing_cycle,
  });
  return response.data;
}

/**
 * Create a Stripe Customer Portal session for billing management
 * @returns {Promise<{portal_url: string}>}
 */
export async function createPortalSession() {
  const response = await apiClient.post("/subscriptions/create-portal-session/", {
    return_url: appAbsoluteUrl("/settings/subscription"),
  });
  return response.data;
}

/**
 * Get subscription activity events for the current tenant
 * @returns {Promise<Array>} List of subscription events
 */
export async function getSubscriptionEvents() {
  const response = await apiClient.get("/subscriptions/events/");
  return response.data;
}

/**
 * Verify subscription status with Stripe in real-time
 * @returns {Promise<Object>} Live Stripe status with attention flags
 */
export async function verifySubscriptionStatus() {
  const response = await apiClient.get("/subscriptions/verify-status/");
  return response.data;
}
